import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  HiCheck,
  HiOutlineArrowDownTray,
  HiOutlineArrowLeft,
  HiOutlineClock,
  HiOutlineSparkles,
  HiOutlineStar,
  HiStar,
} from "react-icons/hi2";
import { FaCopy } from "react-icons/fa";
import { COPY_FEEDBACK_MS } from "./useCopyToClipboard";
import QRCodeGenerator, { QRCodeGeneratorHandle } from "./QRCodeGenerator";
import type { LoyaltyCardPresentationProps } from "../../types/LoyaltyCardTypes";
import { toast } from "react-toastify";
import LoyaltyCardContainer from "./LoyaltyCardContainer";
import SocialAuth from "../pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { CustomButton } from "./CustomButton";
import { useUserSocialLogin } from "../../hooks/users";
import LoyaltyEmailLogin from "../molecules/LoyaltyEmailLogin";
import LmcUserViewForgotPassword from "../pages/lmcUserVIew/LmcUserViewForgotPassword";
import LanguageSelector from "./LanguageSelector";
import { LoyaltyBadgeType } from "../../utils/loyaltyBadgeUtils";
import LoyaltyBadge from "./LoyaltyBadge";
import { getLinkIcon } from "../../utils/businessCardUtils";
import { useNavigate, useParams } from "react-router-dom";
import useIsAuthenticate from "../pages/lmcUserVIew/hooks/useIsAuthenticate";
import BusinessCardFooter from "./BusinessCardFooter";
import { SmartLink } from "./SmartLink";
import PreviewSafeButton from "./PreviewSafeButton";
import LouiseBotWidget from "../widgets/LouiseBotWidget";

const clampChannel = (value: number) => Math.max(0, Math.min(255, value));

const normalizeHex = (hexColor?: string) => {
  if (!hexColor) return null;
  const hex = hexColor.trim();
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  return hex.startsWith("#") ? hex : `#${hex}`;
};

const adjustHexColor = (hex: string, amount: number) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;

  const hexBody = normalized.replace("#", "");
  const num = parseInt(hexBody, 16);

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = clampChannel(r + amount);
  g = clampChannel(g + amount);
  b = clampChannel(b + amount);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const formatDateLabel = (value?: string) => {
  if (!value) return null;
  const date = moment(value);
  if (!date.isValid()) return null;
  return date.format("ll");
};

const createScrollableContainer = (
  children: React.ReactNode,
  className?: string
) => (
  <div className="w-full h-full">
    <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
      <div className="row-start-1 row-end-2 min-h-0">
        <div
          className="h-full overflow-y-auto touch-pan-y"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div
            className={`rounded-xl overflow-hidden shadow-lg border border-white/20 backdrop-blur m-3 sm:m-4 ${
              className ||
              "bg-slate-950/95 flex flex-col gap-4 px-5 sm:px-6 py-6"
            }`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const createBackButton = (onClick: () => void, flipToFrontLabel: string) => (
  <button
    type="button"
    onClick={onClick}
    className="p-2 rounded-full bg-white/10 border border-white/15 text-white hover:bg-white/20 transition"
    title={flipToFrontLabel}
  >
    <HiOutlineArrowLeft className="w-5 h-5" />
  </button>
);

const STAR_COUNT = 5;
const STATIC_FILLED_STARS = 2;
const QR_COUNTDOWN_INTERVAL = 1000;
const STAR_ANIMATION_INTERVAL = 600;
const FLIP_ANIMATION_DELAY = 280;
const FLIP_ANIMATION_DURATION = 600;

const LoyaltyCardDefaultDisplay: React.FC<LoyaltyCardPresentationProps> = ({
  entity,
  program,
  member,
  qr,
  shareUrl,
  rewardPath,
  googleReview,
  entityIdentification,
  widget,
  hideAuth = false,
}) => {
  const { t } = useTranslation("clientView");
  const flipToFrontLabel = t("card.flipToFront") as string;
  const [showBackFace, setShowBackFace] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [qrCountdown, setQrCountdown] = useState<string>("");
  const [linkCopied, setLinkCopied] = useState(false);
  const qrRef = useRef<QRCodeGeneratorHandle>(null);
  const { mutate: socialLogin } = useUserSocialLogin();
  const isGoogleReviewAvailable = Boolean(
    googleReview?.enabled && googleReview.url
  );
  const googleReviewUrl = googleReview?.url;
  const [starActiveCount, setStarActiveCount] =
    useState<number>(STATIC_FILLED_STARS);
  const [isStarAnimationPaused, setIsStarAnimationPaused] =
    useState<boolean>(false);
  const starIndexes = useMemo(
    () => Array.from({ length: STAR_COUNT }, (_, index) => index),
    []
  );

  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticate();
  const { processId } = useParams();

  const googleBrandLetters = useMemo(
    () => [
      { char: "G", color: "#4285F4" },
      { char: "o", color: "#EA4335" },
      { char: "o", color: "#FBBC05" },
      { char: "g", color: "#4285F4" },
      { char: "l", color: "#34A853" },
      { char: "e", color: "#EA4335" },
    ],
    []
  );
  const renderLeaveReviewLabel = useCallback(
    (className?: string) => (
      <span className={className}>
        <span className="inline-flex items-center gap-[1px] font-bold">
          {googleBrandLetters.map(({ char, color }, index) => (
            <span
              key={`google-letter-${index}`}
              style={{ color }}
              className="leading-none"
            >
              {char}
            </span>
          ))}
        </span>
      </span>
    ),
    [googleBrandLetters]
  );

  const accentBase = useMemo(
    () =>
      normalizeHex(program.color) ||
      normalizeHex(entity.accentColor) ||
      "#0ea5e9",
    [entity.accentColor, program.color]
  );

  const gradientStyle = useMemo(() => {
    const fallback = {
      backgroundImage: "linear-gradient(135deg, #0ea5e9, #2563eb)",
    };

    if (!accentBase) return fallback;

    const lighter = adjustHexColor(accentBase, 40) || accentBase;
    const darker = adjustHexColor(accentBase, -35) || accentBase;

    return {
      backgroundImage: `linear-gradient(135deg, ${lighter}, ${darker})`,
    };
  }, [accentBase]);

  const verifiedBadge = useMemo((): LoyaltyBadgeType | null => {
    if (!program.badges?.includes("verified")) {
      return null;
    }
    return {
      type: "verified",
      label: "Verified",
      icon: "verified",
    };
  }, [program.badges]);

  const lastUpdatedLabel = useMemo(
    () => formatDateLabel(member.lastUpdated),
    [member.lastUpdated]
  );

  const userLinks = useMemo(
    () =>
      (program.redirectionLinks || []).filter((link) =>
        Boolean(link?.link && link?.label)
      ),
    [program.redirectionLinks]
  );

  const entityBrand = useMemo(() => {
    if (entity.logo) {
      return (
        <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/40 shadow-sm bg-white">
          <img
            src={entity.logo}
            alt={entity.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      );
    }

    const initial = (entity.name || "?").trim().charAt(0).toUpperCase() || "?";

    return (
      <div className="w-12 h-12 rounded-xl border border-white/40 bg-white/10 flex items-center justify-center text-lg font-semibold text-white shadow-sm">
        {initial}
      </div>
    );
  }, [entity.logo, entity.name]);

  useEffect(() => {
    if (!qr.expiresAt) {
      setQrCountdown("");
      return;
    }

    const updateCountdown = () => {
      if (!qr.isValid) {
        setQrCountdown("");
        return;
      }

      const expiry = moment(qr.expiresAt);
      if (!expiry.isValid()) {
        setQrCountdown("");
        return;
      }

      const diffSeconds = expiry.diff(moment(), "seconds");
      if (diffSeconds <= 0) {
        setQrCountdown("");
        return;
      }

      const minutes = Math.floor(diffSeconds / 60);
      const seconds = diffSeconds % 60;
      setQrCountdown(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      );
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, QR_COUNTDOWN_INTERVAL);

    return () => window.clearInterval(interval);
  }, [qr.expiresAt, qr.isValid]);

  const handleFlipToBack = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    window.setTimeout(() => setShowBackFace(true), FLIP_ANIMATION_DELAY);
    window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
  }, [isFlipping]);

  const handleFlipToFront = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    window.setTimeout(() => {
      setShowBackFace(false);
      setShowForgotPassword(false);
    }, FLIP_ANIMATION_DELAY);
    window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
  }, [isFlipping]);

  const handleShowForgotPassword = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    window.setTimeout(() => setShowForgotPassword(true), FLIP_ANIMATION_DELAY);
    window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
  }, [isFlipping]);

  const handleCopyShareLink = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: program.title || entity.name,
          text: program.comment,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = shareUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), COPY_FEEDBACK_MS);
    } catch (error) {
      if ((error as any)?.name === "AbortError") {
        return;
      }
      toast.error(t("card.shareError"));
    }
  }, [entity.name, program.comment, program.title, shareUrl, t]);

  const handleDownloadQr = useCallback(async () => {
    try {
      const blob = await qrRef.current?.getBlob("png");
      if (!blob) {
        toast.error(t("card.downloadError"));
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `loyalty-qr-${member.id || "guest"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("card.downloadError"));
    }
  }, [member.id, t]);

  const handleResolveSocialAuth = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    const cpPayload = {
      access_token:
        provider === "google"
          ? data?.access_token
          : data?.authorization?.id_token,
      provider,
    };
    socialLogin({
      entity_identification: entityIdentification || entity.name,
      data: { type: "login", providerPayload: cpPayload },
    });
  };

  const handleRejectSocialAuth = (err: any) => {
    console.log(err);
  };

  useEffect(() => {
    if (!isGoogleReviewAvailable) {
      setIsStarAnimationPaused(true);
      setStarActiveCount(STATIC_FILLED_STARS);
      return;
    }

    setIsStarAnimationPaused(false);
    setStarActiveCount(STATIC_FILLED_STARS);
  }, [isGoogleReviewAvailable]);

  useEffect(() => {
    if (!isGoogleReviewAvailable || isStarAnimationPaused) {
      return;
    }

    const interval = window.setInterval(() => {
      setStarActiveCount((prev) => {
        const minAnimatedCount = STATIC_FILLED_STARS + 1;
        if (prev >= STAR_COUNT) {
          return minAnimatedCount;
        }
        if (prev < minAnimatedCount) {
          return minAnimatedCount;
        }
        return prev + 1;
      });
    }, STAR_ANIMATION_INTERVAL);

    return () => window.clearInterval(interval);
  }, [isGoogleReviewAvailable, isStarAnimationPaused]);

  useEffect(() => {
    if (isAuthenticated && !hideAuth && processId) {
      navigate(`/loyalty-program/${processId}/dashboard`, {
        replace: true,
      });
    }
  }, [isAuthenticated, navigate, processId, hideAuth]);

  const frontContent = (
    <div className="w-full h-full">
      <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
        <div className="row-start-1 row-end-2 min-h-0">
          <div
            className="h-full overflow-y-auto touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-slate-950/90 backdrop-blur m-3 sm:m-4">
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0">{entityBrand}</div>
                        <div className="min-w-0 flex-1">
                          <h2
                            className={`${
                              entity.name && entity.name.length > 25
                                ? "text-lg sm:text-xl"
                                : "text-xl sm:text-2xl"
                            } font-semibold text-white leading-tight break-words`}
                          >
                            {entity.name}
                          </h2>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <LanguageSelector variant="compact" />
                      </div>
                    </div>
                    {lastUpdatedLabel && (
                      <div className="text-right text-xs text-white/60 normal-case tracking-normal">
                        {t("card.lastUpdated", {
                          date: lastUpdatedLabel,
                        })}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {entity.description && (
                      <p className="text-sm text-white/70 mt-1">
                        {entity.description}
                      </p>
                    )}
                  </div>
                  {(verifiedBadge ||
                    (isGoogleReviewAvailable && googleReviewUrl)) && (
                    <div className="flex items-center justify-between">
                      <div className="flex-shrink-0">
                        {verifiedBadge && (
                          <LoyaltyBadge badge={verifiedBadge} />
                        )}
                      </div>

                      <div className="flex-shrink-0">
                        {isGoogleReviewAvailable && googleReviewUrl && (
                          <PreviewSafeButton
                            onClick={() =>
                              navigate(`/loyalty-program/${processId}/feedback`)
                            }
                            aria-label={`Google Review - ${entity.name || ""}`}
                            className="group inline-flex items-center rounded-full border border-white/40 bg-white/95 px-2 py-1 text-gray-900 shadow-lg backdrop-blur transition-all duration-200"
                          >
                            <div className="relative flex flex-col items-center">
                              <div className="relative flex items-center">
                                {starIndexes.map((index) => {
                                  const isStaticFilled =
                                    index < STATIC_FILLED_STARS;
                                  const isActive =
                                    starActiveCount > index || isStaticFilled;
                                  const isCurrent =
                                    !isStarAnimationPaused &&
                                    starActiveCount - 1 === index;
                                  const isFinalActive =
                                    isActive && index === STAR_COUNT - 1;

                                  return (
                                    <span
                                      key={`review-star-${index}`}
                                      className="relative flex items-center justify-center h-5 w-5 transition-transform duration-300"
                                      aria-hidden="true"
                                    >
                                      {isFinalActive && (
                                        <>
                                          <span className="absolute inset-[-6px] rounded-full border border-amber-400/80 opacity-70 animate-ping" />
                                          <span className="absolute inset-0 rounded-full bg-amber-300/50 blur-sm" />
                                        </>
                                      )}
                                      <span
                                        className={`transition-transform duration-300 ${
                                          isCurrent ? "scale-110" : "scale-100"
                                        }`}
                                      >
                                        {isActive ? (
                                          <HiStar
                                            className={`text-amber-500 drop-shadow ${
                                              isFinalActive
                                                ? "h-[18px] w-[18px]"
                                                : "h-4 w-4"
                                            }`}
                                          />
                                        ) : (
                                          <HiOutlineStar className="h-4 w-4 text-amber-300" />
                                        )}
                                      </span>
                                    </span>
                                  );
                                })}
                              </div>
                              {renderLeaveReviewLabel(
                                "flex items-center justify-center text-sm font-semibold leading-tight text-amber-900"
                              )}
                            </div>
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-200 text-amber-900 shadow-inner transition-transform duration-200 group-hover:translate-x-0.5">
                              <HiOutlineArrowLeft className="h-4 w-4 rotate-180" />
                            </div>
                          </PreviewSafeButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!hideAuth && (
          <div className="row-start-2 row-end-3 overflow-hidden">
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 relative z-10">
              <div className="w-full flex flex-col gap-2" dir="auto">
                <div className="w-full flex-col justify-between items-center flex">
                  <SocialAuth
                    className="w-full"
                    providers={["Google", "Apple"]}
                    onLoginStartSocialAuthStart={() => {}}
                    onResolveSocialAuth={handleResolveSocialAuth}
                    onRejectSocialAuth={handleRejectSocialAuth}
                    redirectUrl={{
                      Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
                    }}
                    providersIcon={{
                      Apple: (
                        <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                          <img
                            src="../../images/apple.svg"
                            alt=""
                            className="w-5 h-5 absolute left-4"
                          />
                          <p className="text-white text-sm font-semibold font-figtree leading-normal">
                            {t("key18")}
                          </p>
                        </div>
                      ),
                      Google: (
                        <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                          <img
                            src="../../images/google.svg"
                            alt=""
                            className="w-5 h-5 absolute left-4"
                          />
                          <p className="text-white text-sm font-semibold font-figtree leading-normal">
                            {t("key19")}
                          </p>
                        </div>
                      ),
                    }}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <p
                    className="text-center text-gray-800 text-xs font-medium font-figtree uppercase leading-none"
                    dir="auto"
                  >
                    {t("key20")}
                  </p>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>

                <CustomButton
                  variant="primary"
                  className="w-full bg-sky-500 text-white enabled:hover:bg-sky-600"
                  title={t("key25") as string}
                  onClick={handleFlipToBack}
                />
              </div>
            </div>
          </div>
        )}

        {userLinks.length > 0 && (
          <div className="row-start-3 row-end-4 min-h-0">
            <div
              className="h-full overflow-y-auto touch-pan-y"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-slate-950/90 backdrop-blur m-3 sm:m-4">
                <div className="relative px-2 pt-5 pb-6 sm:px-2 sm:pt-6 sm:pb-7 flex flex-col">
                  <div
                    className="absolute inset-0 opacity-90"
                    style={gradientStyle}
                  />
                  <div className="relative z-10 flex flex-col gap-6 w-full">
                    <div className="space-y-2" dir="auto">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-white/60">
                        {t("card.quickLinks")}
                      </p>
                      {userLinks.map((link) => (
                        <SmartLink
                          key={link.id || link.link}
                          href={link.link}
                          fallbackHref={link.fallbackLink}
                          className="flex items-center justify-between rounded-xl bg-black/25 border border-white/15 px-2 py-3 text-white transition-all duration-200 hover:scale-[1.02] hover:bg-black/35"
                          dir="auto"
                        >
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                              {getLinkIcon(link, "text-gray-600")}
                            </div>
                            <span className="text-sm font-medium truncate">
                              {link.label}
                            </span>
                          </div>
                          <HiOutlineArrowLeft className="w-4 h-4 rotate-180 flex-shrink-0 ml-2" />
                        </SmartLink>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {widget?.enabled && widget?.bot_id && widget?.mode === "inline" && (
          <div className="row-start-4 row-end-5 min-h-0">
            <div
              className="h-full overflow-y-auto touch-pan-y"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="mx-3 sm:mx-4 mb-3 sm:mb-4 flex justify-end">
                <LouiseBotWidget
                  botId={widget.bot_id}
                  mode="inline"
                  containerId="louisebot-loyalty-card-widget"
                  hideCloseButton={true}
                  hideMinimizeButton={true}
                />
              </div>
            </div>
          </div>
        )}

        <div className="row-start-5 row-end-6">
          <BusinessCardFooter showFooter={true} theme="default" />
        </div>
      </div>
    </div>
  );

  const emailLoginContent = createScrollableContainer(
    <>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-white">{t("key25")}</h3>
        </div>
        {createBackButton(handleFlipToFront, flipToFrontLabel)}
      </div>

      <div className="flex-1 flex flex-col gap-4">
        <LoyaltyEmailLogin onForgotPassword={handleShowForgotPassword} />
      </div>
    </>
  );

  const forgotPasswordContent = createScrollableContainer(
    <>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-white">{t("key57")}</h3>
          <p className="text-sm text-white/70 leading-relaxed mt-1">
            {t("key66")}
          </p>
        </div>
        {createBackButton(handleFlipToFront, flipToFrontLabel)}
      </div>

      <div className="flex-1 flex flex-col gap-4">
        <LmcUserViewForgotPassword />
      </div>
    </>
  );

  const qrContent = createScrollableContainer(
    <>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t("card.scanInstructions")}
          </h3>
          <p className="text-sm text-white/70 leading-relaxed mt-1">
            {t("card.scanHint")}
          </p>
        </div>
        {createBackButton(handleFlipToFront, flipToFrontLabel)}
      </div>

      <div className="flex-1 flex items-center justify-center min-h-[240px]">
        {qr.isValid ? (
          <QRCodeGenerator
            ref={qrRef}
            data={qr.data}
            size={240}
            showLogo={Boolean(qr.logoUrl)}
            logoUrl={qr.logoUrl}
            fgColor="#000000"
            bgColor="#FFFFFF"
            className="bg-white/5 rounded-xl p-3"
            ariaLabel={t("card.qrAria") as string}
          />
        ) : (
          <div className="text-center text-white/70 text-sm leading-relaxed">
            {t("card.invalidQr")}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-white/80 text-sm flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HiOutlineClock className="w-5 h-5" />
          {qr.isValid && qrCountdown ? (
            <span>
              {t("card.qrExpiresIn")}: {qrCountdown}
            </span>
          ) : (
            <span>{t("card.qrExpired")}</span>
          )}
        </div>
        {!qr.isValid && qr.onRefresh && (
          <button
            type="button"
            onClick={qr.onRefresh}
            className="px-3 py-1.5 rounded-full bg-white text-gray-900 font-semibold text-xs hover:bg-gray-100 transition"
          >
            {t("card.refresh")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleDownloadQr}
          className="flex items-center justify-center gap-2 rounded-xl bg-white text-gray-900 font-semibold text-sm py-3 shadow-sm hover:shadow-lg transition-all duration-200 hover:scale-105"
        >
          <HiOutlineArrowDownTray className="w-5 h-5" />
          <span>{t("card.download")}</span>
        </button>
        <button
          type="button"
          onClick={handleCopyShareLink}
          className="flex items-center justify-center gap-2 rounded-xl bg-black/80 border border-white/20 text-white font-semibold text-sm py-3 shadow-lg hover:bg-black transition-all duration-200 hover:scale-105"
        >
          {linkCopied ? (
            <HiCheck className="w-5 h-5 text-green-400" />
          ) : (
            <FaCopy className="w-5 h-5" />
          )}
          <span>{linkCopied ? t("card.linkCopied") : t("card.copyLink")}</span>
        </button>
      </div>

      {isGoogleReviewAvailable && googleReviewUrl && (
        <button
          onClick={() => navigate(`/loyalty-program/${processId}/feedback`)}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-amber-500 text-amber-950 font-semibold text-sm py-3 shadow-sm transition-all duration-200 hover:scale-105 hover:bg-amber-400"
        >
          <HiOutlineSparkles className="w-5 h-5" />
          {renderLeaveReviewLabel("text-sm font-semibold")}
        </button>
      )}
    </>
  );

  const backContent = showForgotPassword
    ? forgotPasswordContent
    : showBackFace
    ? emailLoginContent
    : qrContent;

  return (
    <LoyaltyCardContainer
      frontContent={frontContent}
      backContent={backContent}
      isPreview={false}
      isFlipping={isFlipping}
      showEmailLogin={showBackFace}
      showForgotPasswordForm={showForgotPassword}
      onFlip={setShowBackFace}
      className="max-w-[360px] w-full"
      fullHeight={true}
    />
  );
};

export default LoyaltyCardDefaultDisplay;
